
import { defineComponent, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import AutoGraphFilters from '@/modules/TMSModule/components/AutoGraphFilters/index.vue'
import GraphBoard from '@/modules/TMSModule/components/GraphBoard/index.vue'
import ModalPanel from '@/modules/NewDispatcher/components/ModalPanel/index.vue'
import PreTripDetails from '@/modules/TMSModule/components/PreTripDetails/index.vue'
import PreTripTransferModal from '@/modules/TMSModule/components/PreTripTransferModal/index.vue'
import PreTripUpdateCommentModal from '@/modules/TMSModule/components/PreTripUpdateCommentModal/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @types
import { preTrip, PreTripDetailsRescheduleHistoryItem } from '@/core/types/tms-graph.types'

// @realisations
import { tmsGraph } from '@/core/realisations/tms-graph.realisation'
import { useFile } from '@/common/composable/useFile'
import { useFormData } from '@/common/composable/useFormData'

export default defineComponent({
    components: {
        PageTitle,
        AutoGraphFilters,
        GraphBoard,
        ModalPanel,
        PreTripDetails,
        PreTripTransferModal,
        PreTripUpdateCommentModal,
        NewButton,
        NewModal,
        FileReader,
    },
    setup() {
        const notifications = useNotifications()
        const route = useRoute()
        const { saveFile } = useFile()
        const { getFormData } = useFormData()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_excel_loading: false,
            is_schedule: false,
            is_schedule_loading: false,
            items: [] as preTrip[],
            file: [] as File[],
        })

        const filters = reactive({
            dateFrom: null as string | null,
            dateTo: null as string | null,
            fromCity: null as number | null,
            whereCity: null as number | null,
            statusId: null as number | null,
            logistId: null as number | null,
        })

        const details_state = reactive({
            id: null as number | string | null,
            opened: false,
        })

        const transfer_state = reactive({
            id: null as number | string | null,
            opened: false,
        })

        const update_comment_state = reactive({
            item: null as PreTripDetailsRescheduleHistoryItem | null,
            opened: false,
        })

        const loadPreTrips = async () => {
            state.items = []

            try {
                state.is_loading = true

                const response = await tmsGraph.getPreTrips({
                    dateFrom: filters.dateFrom as string,
                    dateTo: filters.dateTo as string,
                    fromCityId: filters.fromCity,
                    toCityId: filters.whereCity,
                    statusId: filters.statusId,
                    logistId: filters.logistId,
                })

                state.items = response.data

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении данных произошла ошибка' })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const onApplyFilters = ({
            period,
            fromCity,
            whereCity,
            statusId,
            logistId,
        }: {
            period: string[]
            fromCity: number
            whereCity: number
            statusId: number
            logistId: number
        }) => {
            filters.dateFrom = period[0]
            filters.dateTo = period[1]
            filters.fromCity = fromCity
            filters.whereCity = whereCity
            filters.statusId = statusId
            filters.logistId = logistId

            loadPreTrips()
        }

        const onResetFilters = () => {
            filters.dateFrom = null
            filters.dateTo = null
            filters.fromCity = null
            filters.whereCity = null
            filters.statusId = null
            filters.logistId = null
        }

        const openDetails = ({ id }: { id: number | string }) => {
            details_state.id = id
            details_state.opened = true
        }

        const closeDetails = () => {
            details_state.opened = false
            details_state.id = null
        }

        const openTransfer = ({ id }: { id: number | string }) => {
            transfer_state.id = id
            transfer_state.opened = true
        }

        const closeTransfer = () => {
            transfer_state.opened = false
            transfer_state.id = null
        }

        const onTransferRescheduled = () => {
            closeDetails()
            closeTransfer()
            loadPreTrips()
        }

        const openUpdateComment = (item: PreTripDetailsRescheduleHistoryItem) => {
            update_comment_state.item = item
            update_comment_state.opened = true
        }

        const closeUpdateComment = () => {
            update_comment_state.opened = false
            update_comment_state.item = null
        }

        const onCommentUpdated = () => {
            closeDetails()
            closeUpdateComment()
            loadPreTrips()
        }

        const downloadReport = async () => {
            try {
                state.is_excel_loading = true

                const params = {
                    ...filters,
                }

                const response = await tmsGraph.downloadReportAutoGraph(params)
                saveFile(response, `Отчет по графикам отправок`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При выгрузке отчета произошла ошибка' })
            } finally {
                state.is_excel_loading = false
            }
        }

        const toggleSchedule = () => {
            state.is_schedule = !state.is_schedule
            state.file = []
        }

        const createSchedule = async () => {
            if (!state.file.length) {
                notifications.notify({
                    title: 'Пожалуйста, загрузите Ecel файл',
                    type: 'danger',
                })
                return
            }

            try {
                state.is_schedule_loading = true

                const payload = {
                    file: state.file[0],
                }
                await tmsGraph.createScheduleExcel(getFormData(payload))

                notifications.notify({
                    title: 'Расписание предрейсов успешно создано!',
                    type: 'success',
                })
                toggleSchedule()
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Произошла ошибка' })
            } finally {
                state.is_schedule_loading = false
            }
        }

        onMounted(() => {
            if (route.query.id) {
                openDetails({ id: Number(route.query.id as string) })
            }
        })

        return {
            state,
            filters,
            details_state,
            transfer_state,
            update_comment_state,
            onApplyFilters,
            onResetFilters,
            openDetails,
            closeDetails,
            openTransfer,
            closeTransfer,
            onTransferRescheduled,
            downloadReport,
            toggleSchedule,
            createSchedule,
            openUpdateComment,
            closeUpdateComment,
            onCommentUpdated
        }
    },
})
